import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import { AppLayout } from './layout';
import { ApiHttpError } from './lib/api';
import { loadMe } from './lib/me';
import { supabase } from './lib/supabase';
import { AlertsPage } from './pages/Alerts';
import { AppointmentTypesPage } from './pages/AppointmentTypes';
import { BillingPage } from './pages/Billing';
import { CatalogPage } from './pages/Catalog';
import { CompaniesPage } from './pages/Companies';
import { CompanyDetailPage } from './pages/CompanyDetail';
import { DealDetailPage } from './pages/DealDetail';
import { PipelinePage } from './pages/Pipeline';
import { TodoPage } from './pages/Todo';
import { ClientDetail } from './pages/ClientDetail';
import { ClientsPage } from './pages/Clients';
import { ForbiddenPage } from './pages/Forbidden';
import { LoginPage } from './pages/Login';
import { OverviewPage } from './pages/Overview';
import { PasskeyOfferPage } from './pages/PasskeyOffer';
import { RequestDetail } from './pages/RequestDetail';
import { RequestsPage } from './pages/Requests';
import { SetPasswordPage } from './pages/SetPassword';
import { SettingsPage } from './pages/Settings';
import { TeamPage } from './pages/Team';
import { UseCasesPage } from './pages/UseCases';
import { WebhooksPage } from './pages/Webhooks';

/** Signed in and member of the team; otherwise to the login page or the 403 page (PRD 4.1). */
async function requireMember(): Promise<void> {
  const { data } = await supabase.auth.getSession();
  if (!data.session) {
    throw redirect({ to: '/login' });
  }
  try {
    await loadMe();
  } catch (error) {
    if (error instanceof ApiHttpError && error.status === 403) {
      throw redirect({ to: '/403' });
    }
    throw error;
  }
}

const rootRoute = createRootRoute({ component: () => <Outlet /> });

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
});

const forbiddenRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/403',
  component: ForbiddenPage,
});

/** Pathless layout route guarding operator access (PRD 4.1). */
const protectedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'protected',
  beforeLoad: requireMember,
  component: AppLayout,
});

/** Page where an invited member chooses their password (lot 20); needs the invitation session. */
const setPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/definir-mot-de-passe',
  component: SetPasswordPage,
});

/**
 * Step after a password sign-in: offers a passkey to a member who has none.
 * Same guard as the console, so an outsider is not offered one.
 */
const passkeyOfferRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/proposer-passkey',
  beforeLoad: requireMember,
  component: PasskeyOfferPage,
});

/**
 * Pathless layout route for the admin pages (lot 20): an employee only works
 * the CRM, so anything else sends them to their pipeline. The API enforces the
 * same rule (403); this only spares them a page of errors.
 */
const adminRoute = createRoute({
  getParentRoute: () => protectedRoute,
  id: 'admin',
  beforeLoad: async () => {
    const me = await loadMe();
    if (!me.permissions.includes('operations')) {
      throw redirect({ to: '/pipeline' });
    }
  },
  component: () => <Outlet />,
});

const overviewRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/',
  component: OverviewPage,
});
const clientsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/clients',
  component: ClientsPage,
});
const clientDetailRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/clients/$clientId',
  component: function ClientDetailRoute() {
    const { clientId } = clientDetailRoute.useParams();
    return <ClientDetail clientId={clientId} />;
  },
});
const requestsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/requests',
  component: RequestsPage,
});
const requestDetailRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/requests/$requestId',
  component: function RequestDetailRoute() {
    const { requestId } = requestDetailRoute.useParams();
    return <RequestDetail requestId={requestId} />;
  },
});
const pipelineRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/pipeline',
  component: PipelinePage,
});
const companiesRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/companies',
  component: CompaniesPage,
});
const companyDetailRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/companies/$companyId',
  component: function CompanyDetailRoute() {
    const { companyId } = companyDetailRoute.useParams();
    return <CompanyDetailPage key={companyId} companyId={companyId} />;
  },
});
const dealDetailRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/deals/$dealId',
  component: function DealDetailRoute() {
    const { dealId } = dealDetailRoute.useParams();
    return <DealDetailPage key={dealId} dealId={dealId} />;
  },
});
const todoRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/todo',
  component: TodoPage,
});
const catalogRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/catalog',
  component: CatalogPage,
});
const useCasesRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/use-cases',
  component: UseCasesPage,
});
const billingRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/billing',
  component: BillingPage,
});
const webhooksRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/webhooks',
  component: WebhooksPage,
});
const alertsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/alerts',
  component: AlertsPage,
});
const settingsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/settings',
  component: SettingsPage,
});
const appointmentTypesRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/appointment-types',
  component: AppointmentTypesPage,
});
const teamRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/team',
  component: TeamPage,
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  forbiddenRoute,
  setPasswordRoute,
  passkeyOfferRoute,
  protectedRoute.addChildren([
    pipelineRoute,
    companiesRoute,
    companyDetailRoute,
    dealDetailRoute,
    todoRoute,
    settingsRoute,
    adminRoute.addChildren([
      overviewRoute,
      clientsRoute,
      clientDetailRoute,
      requestsRoute,
      requestDetailRoute,
      catalogRoute,
      useCasesRoute,
      billingRoute,
      webhooksRoute,
      alertsRoute,
      teamRoute,
      appointmentTypesRoute,
    ]),
  ]),
]);

export const router = createRouter({ routeTree });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
